import { conversionEventId } from "@repo/shared";

import { hasGaClientCookie, newEventId, sendCtaBeacon, sendFunnelBeacon } from "../visits/beacon";

import { trackClarityEvent, trackClientEvent, trackConversion } from "./tracker";

import type { EngagedReason, FunnelCta } from "../visits/beacon";

/**
 * Los eventos del embudo, con el `event_id` que arma la API para la misma conversión
 * (`conversionEventId` de `@repo/shared`). Nunca tiran: medir no puede romper un flujo.
 */

/**
 * GA4 de los eventos de la escalera: si al salir el beacon hay cookie `_ga`, lo manda sólo la API
 * (GA4 no deduplica el tag contra el Measurement Protocol); si no hay (primera visita: gtag carga
 * después), lo manda sólo el navegador y el beacon le avisa a la API (`gaBrowser`). Se decide UNA
 * vez por evento, antes del beacon, y vale para los dos lados.
 */
const gaGoesByBrowser = () => {
  try {
    return !hasGaClientCookie();
  } catch {
    return true;
  }
};

const safe = (fn: () => void) => {
  try {
    fn();
  } catch {
    // La medición nunca rompe un registro, una inscripción ni una compra.
  }
};

/** `POST /api/auth/send-otp` devuelve `leadEventId` sólo en el primer intento de registro. */
export function trackLead(leadEventId: string | null | undefined) {
  if (!leadEventId) return;
  safe(() => trackConversion({ name: "lead", eventId: leadEventId }));
}

/**
 * Se mandó el código de un REGISTRO (no de un ingreso): paso «pidió código» del embudo en Clarity.
 * Va en cada pedido, no sólo en el primero como el Lead.
 */
export function trackOtpRequested() {
  safe(() => trackClarityEvent("otp_pedido"));
}

/**
 * Tocó un CTA del embudo («Inscribirme gratis» de la barra fija o la tarjeta, «Registrate» del
 * header). Un solo id para todo:
 *
 * - clic propio (registro de visitas, sin depender de Clarity ni del consentimiento), que la API
 *   además reenvía a Meta, TikTok y GA4 aunque el píxel esté bloqueado;
 * - el píxel del navegador (CtaInscripcion en Meta, ClickButton en TikTok, `cta_click` en GA4) con
 *   el mismo `event_id` (`cta:<id>`): las plataformas cuentan uno;
 * - el evento de Clarity para filtrar grabaciones.
 */
export function trackCtaClick(cta: FunnelCta, courseId?: string | null) {
  const eventId = newEventId();
  const gaBrowser = gaGoesByBrowser();
  safe(() => sendCtaBeacon(cta, eventId, courseId ?? undefined, gaBrowser));
  safe(() =>
    trackClientEvent({
      name: "cta_click",
      eventId: conversionEventId.ctaClick(eventId),
      gaBrowser,
      cta,
      ...(courseId ? { courseId } : {}),
    }),
  );
  safe(() => trackClarityEvent(cta === "header_registrate" ? "cta_registrate" : "cta_inscripcion"));
}

/**
 * Visita comprometida en la página de un curso (≥ 30 s activo o ≥ 50 % de scroll; ver
 * `engagement.ts`). Una vez por vista de página: beacon propio (→ API → plataformas) y píxel, con
 * el mismo id.
 */
export function trackEngagedVisit(reason: EngagedReason, courseId?: string | null) {
  const eventId = newEventId();
  const gaBrowser = gaGoesByBrowser();
  safe(() =>
    sendFunnelBeacon({
      kind: "engaged",
      eventId,
      reason,
      courseId: courseId ?? undefined,
      gaBrowser,
    }),
  );
  safe(() =>
    trackClientEvent({
      name: "engaged_visit",
      eventId: conversionEventId.engagedVisit(eventId),
      gaBrowser,
      reason,
      ...(courseId ? { courseId } : {}),
    }),
  );
}

export function trackSignUp(userId: string | null | undefined) {
  if (!userId) return;
  safe(() => trackConversion({ name: "sign_up", eventId: conversionEventId.signUp(userId) }));
}

/** Una conversión por curso otorgado en la orden gratis (igual que la API). */
export function trackFreeEnrollment(orderId: string | null | undefined, courseIds: string[]) {
  if (!orderId) return;
  safe(() => trackClarityEvent("inscripto"));
  safe(() => {
    for (const courseId of courseIds) {
      trackConversion({
        name: "enroll_free",
        eventId: conversionEventId.enrollFree(orderId, courseId),
        courseId,
      });
    }
  });
}

export function trackPurchase(order: {
  orderId: string;
  valueInCents: number;
  currency: string;
  courseIds: string[];
}) {
  if (!order.orderId || order.valueInCents <= 0) return;
  safe(() =>
    trackConversion({
      name: "purchase",
      eventId: conversionEventId.purchase(order.orderId),
      ...order,
    }),
  );
}

/**
 * Vio la página de un curso: píxel (ViewContent / view_item) y beacon propio con el mismo id, para
 * que la API lo mande también server-side (`vc:<id>`).
 */
export function trackCourseView(course: {
  id: string;
  title: string;
  priceInCents?: number | null;
  currency?: string | null;
}) {
  const eventId = newEventId();
  const gaBrowser = gaGoesByBrowser();
  safe(() => sendFunnelBeacon({ kind: "view", eventId, courseId: course.id, gaBrowser }));
  safe(() =>
    trackClientEvent({
      name: "view_content",
      eventId: conversionEventId.viewContent(eventId),
      gaBrowser,
      courseId: course.id,
      courseTitle: course.title,
      valueInCents: course.priceInCents ?? null,
      currency: course.currency ?? null,
    }),
  );
}

export function trackCheckoutStarted(checkout: {
  courseIds: string[];
  valueInCents: number;
  currency: string;
}) {
  if (!checkout.courseIds.length) return;
  safe(() => trackClientEvent({ name: "initiate_checkout", ...checkout }));
}
